import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FreeResourcesBar from '../components/FreeResourcesBar';
import { CheckCircle, Award, Clock, Users, Calendar, Download, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';

const AISolutionArchitectPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const toggleFAQ = (id) => {
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const syllabus = [
    { week: "Week 1", title: "Evolution of Intelligent Systems", topics: ["AI history from ML to GenAI", "Case Studies: ELIZA, Deep Blue, AlphaGo, ChatGPT", "Traditional ML vs Generative AI", "Cloud Solution Architect role"] },
    { week: "Week 2", title: "GenAI Core Concepts & Prompt Engineering", topics: ["GenAI models and providers", "Cloud platform setup", "Advanced prompting techniques", "Zero-shot, few-shot, chain-of-thought"] },
    { week: "Week 3", title: "Solution Patterns & Data Engineering", topics: ["Fine-tuning vs RAG architecture", "Implementing RAG pipelines", "Chunking strategies & embeddings", "Vector databases"] },
    { week: "Week 4", title: "Responsible AI & Building POCs", topics: ["AI ethics and regulations", "Responsible AI frameworks", "Creating working GenAI solutions", "RAG POC architecture best practices"] },
    { week: "Week 5", title: "Industry Use Cases & Production Challenges", topics: ["Real-world GenAI applications", "Public & private sector examples", "Why POCs fail to reach production", "Technical and business barriers"] },
    { week: "Week 6", title: "Single & Multi-Agent Architectures", topics: ["Agent definition and use cases", "Collaboration patterns", "Coordination strategies", "Agentic solution design"] },
    { week: "Week 7", title: "AI Landing Zones & GenAI Ops", topics: ["Cloud landing zones for AI", "Security, governance, compliance", "CI/CD for AI models", "MLOps vs GenAI Ops lifecycle"] },
    { week: "Week 8", title: "Production Optimization & Lifecycle", topics: ["Performance & scalability", "Cost optimization strategies", "Monitoring and alerting", "Post-go-live management"] },
    { week: "Week 9", title: "Capstone Presentations & Career Outlook", topics: ["Final project presentations", "Peer feedback sessions", "AI Solution Architect career path", "Skills in demand & interview prep"] }
  ];

  const faqs = [
    {
      id: 1,
      question: "What is the AI Solution Architect course?",
      answer: "This is a comprehensive 9-week program that teaches you to design and deliver end-to-end GenAI solutions, from proof of concept to production-ready architecture. You'll master RAG pipelines, agentic architectures, prompt engineering, and responsible AI frameworks."
    },
    {
      id: 2,
      question: "Who is this course for?",
      answer: "This course is ideal for mid-level solution architects wanting to master GenAI, technical professionals stepping into senior roles, and senior architects seeking structured frameworks for the latest GenAI architectures. Basic cloud and data fundamentals knowledge is helpful."
    },
    {
      id: 3,
      question: "What projects will I build?",
      answer: "You'll complete 5 assignments and 5 workshops, culminating in a capstone project where you design and implement a working RAG-based GenAI solution with responsible AI considerations. You'll create a narrative presentation and demo video showcasing your architecture."
    },
    {
      id: 4,
      question: "Will I get a certificate?",
      answer: "Yes! Upon successful completion of the course and capstone project, you'll receive a professional certificate that you can showcase on LinkedIn and your resume."
    },
    {
      id: 5,
      question: "What's the time commitment?",
      answer: "The course runs for 9 weeks with live sessions twice a week (Tuesdays & Thursdays). Additionally, you'll need time for self-study activities and assignments between sessions."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <FreeResourcesBar />

      {/* Hero Section - Elegant Palette */}
      <section className="pt-24 pb-16 bg-[#1E293B] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#4F46E5] text-white px-6 py-2 rounded-full font-bold mb-6">
              <Lightbulb className="w-5 h-5" />
              <span>9-Week Live Online Course</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
              AI Solution Architect Course
            </h1>

            {/* Description */}
            <p className="text-xl md:text-2xl text-slate-700 mb-8 leading-relaxed">
              Learn how to design and deliver end-to-end GenAI solutions, from proof of concept to production-ready architecture. Master RAG pipelines, agentic architectures, and responsible AI frameworks.
            </p>

            <p className="text-lg text-slate-600 mb-8">
              <strong>Led by industry expert with 22+ years experience in AI, Cloud & DevOps</strong>
            </p>

            {/* Key Info Badges */}
            <div className="flex flex-wrap gap-4 justify-center mb-8">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span className="font-semibold">Starts January 13, 2026</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-semibold">9 weeks | Tuesdays & Thursdays</span>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/20">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">5:30 PM AEDT</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300"
              >
                Apply Now
              </button>
              <button className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-bold px-10 py-4 rounded-xl border border-white/20 transition-all duration-300">
                <Download className="w-5 h-5 inline mr-2" />
                Download Course Info
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Course Is For */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              This course is for you, if...
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: "YOU ARE A MID-LEVEL SOLUTION ARCHITECT", 
                desc: "Overwhelmed by the pace of AI? You know cloud and data fundamentals, but the rapid evolution of GenAI can feel impossible to keep up with. This course gives you a structured methodology to master GenAI building blocks like RAG pipelines, agentic architectures, and prompt engineering."
              },
              { 
                title: "YOU ARE A TECHNICAL PRO", 
                desc: "Want to step up into senior roles but lack comprehensive understanding of AI architectures? This course fast-tracks your learning, giving you strategic and technical expertise. By the end, you'll be ready to design end-to-end GenAI solutions."
              },
              { 
                title: "YOU ARE A SENIOR ARCHITECT", 
                desc: "Extensive experience in AI but limited exposure to the latest GenAI architectures? Get a structured framework for scaling AI responsibly, from initial business ideation to production deployment. Learn to lead teams and implement cutting-edge agentic systems."
              }
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-50 to-teal-50 rounded-2xl p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-lg font-bold text-teal-700 mb-4">{item.title}</h3>
                <p className="text-slate-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You'll Do */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What You'll Do
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">HANDS-ON LEARNING</h3>
              <p className="text-slate-600 text-center">Work through 5 assignments and 5 workshops. Learn prompt engineering, implement RAG pipelines, manage vector databases, and assemble a working GenAI POC.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">REAL-WORLD CASE STUDIES</h3>
              <p className="text-slate-600 text-center">Analyse GenAI projects from ChatGPT to multi-agent systems. Learn frameworks that industry leaders use to deliver AI responsibly at scale.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg mx-auto">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3 text-center">FINAL PROJECT</h3>
              <p className="text-slate-600 text-center">Deliver a story-driven, technically rigorous POC presentation. Show your journey from identifying an industry problem to creating a production-ready GenAI solution.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Syllabus */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Course Syllabus</h2>
            <p className="text-xl text-slate-600">9 weeks of intensive, hands-on learning</p>
          </div>

          <div className="space-y-4">
            {syllabus.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 text-white font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-bold text-teal-700">{item.week}</span>
                      <h3 className="text-xl font-bold text-white">{item.title}</h3>
                    </div>
                    <ul className="space-y-1">
                      {item.topics.map((topic, topicIdx) => (
                        <li key={topicIdx} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-10 py-4 rounded-xl shadow-lg transition-all duration-300">
              <Download className="w-5 h-5 inline mr-2" />
              Download full syllabus
            </button>
          </div>
        </div>
      </section>

      {/* Companies Our Students Work At */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-8">Where Our Students Can Work</h3>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" alt="Google" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg" alt="Microsoft" className="h-8" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" alt="Amazon" className="h-10" />
            <img src="https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg" alt="Meta" className="h-8" />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Frequently asked questions</h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-900">{faq.question}</span>
                  {openFAQ === faq.id ? (
                    <ChevronUp className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                  )}
                </button>
                {openFAQ === faq.id && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <p className="text-slate-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#1E293B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to become an AI Solution Architect?
          </h2>
          <p className="text-xl text-slate-700 mb-8">Starting January 13, 2026 | 9 weeks | Tuesdays & Thursdays</p>
          <button
            onClick={() => window.location.href = '/contact'}
            className="bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold px-12 py-5 rounded-xl shadow-2xl transition-all duration-300 text-lg"
          >
            Apply Now →
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AISolutionArchitectPage;
